import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ProcessingRouteState, ThreatCoreResponse } from "../types";
import {
  streamThreatCore,
  callThreatCore,
  identifyThreatSources,
} from "../services/threat_model_services";

// ── Pipeline definition ───────────────────────────────────────────────────────
// Steps 1–4 are driven by /core/stream progress events (display only).
// Step 5 is driven by /api/threat-sources/identify (called after /api/threat/core).

const PIPELINE = [
  {
    id: "asset_extraction",
    label: "Asset Extraction",
    description: "Identifying all system components and trust boundaries",
  },
  {
    id: "data_flow",
    label: "Data Flow Analysis",
    description: "Mapping data flows between system components",
  },
  {
    id: "trust_boundary",
    label: "Trust Boundary Analysis",
    description: "Defining security perimeters and access control zones",
  },
  {
    id: "threat_sources_identify",
    label: "Threat Sources Identification",
    description: "Deep-dive identification of threat actors and attack vectors",
  },
];

// ── Badges ────────────────────────────────────────────────────────────────────

const CriticalityBadge: React.FC<{ value: string }> = ({ value }) => {
  const cls =
    value === "High"
      ? "bg-red-50 text-red-700 border-red-200"
      : value === "Medium"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-green-50 text-green-700 border-green-200";
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${cls}`}>
      {value}
    </span>
  );
};

const SeverityBadge: React.FC<{ value: string }> = ({ value }) => {
  const cls =
    value === "high"
      ? "bg-red-50 text-red-700 border-red-200"
      : value === "medium"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-green-50 text-green-700 border-green-200";
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${cls} capitalize`}>
      {value}
    </span>
  );
};

// ── Step detail renderers (data from /api/threat/core) ────────────────────────

const AssetExtractionDetail: React.FC<{ results: any }> = ({ results }) => {
  const data = results?.assets;
  if (!data) return null;
  return (
    <div className="mt-3">
      <p className="text-xs text-gray-500 mb-2">
        <span className="font-semibold text-gray-800">{data.total_count ?? data.assets?.length ?? 0}</span> assets identified
        {data.extraction_confidence !== undefined && (
          <> · Confidence: <span className="font-semibold text-purple-600">{Math.round(data.extraction_confidence * 100)}%</span></>
        )}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {(data.assets ?? []).map((a: any, i: number) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-100">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
            {a.name}
          </span>
        ))}
      </div>
    </div>
  );
};

const DataFlowDetail: React.FC<{ results: any }> = ({ results }) => {
  const df = results?.data_flows;
  if (!df) return null;
  const flows: any[] = df.Dataflows ?? [];
  return (
    <div className="mt-3">
      <p className="text-xs text-gray-500 mb-2">
        <span className="font-semibold text-gray-800">{df.total_flows ?? flows.length}</span> flows identified
        {df.flow_confidence !== undefined && (
          <> · Confidence: <span className="font-semibold text-purple-600">{Math.round(df.flow_confidence * 100)}%</span></>
        )}
      </p>
      <div className="overflow-x-auto rounded-lg border border-gray-100">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 text-left border-b border-gray-100">
              <th className="px-3 py-2 font-semibold text-gray-500">Source</th>
              <th className="px-3 py-2 font-semibold text-gray-500">Target</th>
              <th className="px-3 py-2 font-semibold text-gray-500">Type</th>
              <th className="px-3 py-2 font-semibold text-gray-500">Criticality</th>
            </tr>
          </thead>
          <tbody>
            {flows.map((flow, i) => (
              <tr key={i} className="border-t border-gray-50 hover:bg-gray-50 transition">
                <td className="px-3 py-2 font-medium text-gray-800">{flow.source_entity}</td>
                <td className="px-3 py-2 text-gray-600">{flow.target_entity}</td>
                <td className="px-3 py-2">
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">{flow.flow_type}</span>
                </td>
                <td className="px-3 py-2">
                  <CriticalityBadge value={flow.criticality} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TrustBoundaryDetail: React.FC<{ results: any }> = ({ results }) => {
  const tb = results?.trust_boundaries;
  if (!tb) return null;
  const boundaries: any[] = tb.boundaries ?? [];
  return (
    <div className="mt-3">
      <p className="text-xs text-gray-500 mb-2">
        <span className="font-semibold text-gray-800">{tb.total_boundaries ?? boundaries.length}</span> boundaries identified
        {tb.boundary_confidence !== undefined && (
          <> · Confidence: <span className="font-semibold text-purple-600">{Math.round(tb.boundary_confidence * 100)}%</span></>
        )}
      </p>
      <div className="space-y-2">
        {boundaries.map((b, i) => (
          <div key={i} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-xs text-gray-800">{b.source_entity}</span>
              <span className="text-gray-400 text-xs">→</span>
              <span className="font-medium text-xs text-gray-800">{b.target_entity}</span>
              <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">{b.boundary_type}</span>
            </div>
            {b.security_controls?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {b.security_controls.map((ctrl: string, j: number) => (
                  <span key={j} className="inline-block px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-100">{ctrl}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* const ThreatSourceDetail: React.FC<{ results: any }> = ({ results }) => {
  const ts = results?.threat_sources;
  if (!ts) return null;
  const vectors: any[] = ts.attack_vectors ?? [];
  const summary = ts.severity_summary;
  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-4 mb-2">
        <span className="text-xs text-gray-500">
          <span className="font-semibold text-gray-800">{ts.total_threats ?? vectors.length}</span> threats identified
        </span>
        {summary && (
          <div className="flex items-center gap-2 text-xs">
            {summary.high > 0 && <span className="px-2 py-0.5 rounded border bg-red-50 text-red-700 border-red-200">{summary.high} High</span>}
            {summary.medium > 0 && <span className="px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">{summary.medium} Medium</span>}
            {summary.low > 0 && <span className="px-2 py-0.5 rounded border bg-green-50 text-green-700 border-green-200">{summary.low} Low</span>}
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        {vectors.map((v, i) => (
          <div key={i} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 flex items-start justify-between gap-2 flex-wrap">
            <span className="font-medium text-xs text-gray-800 flex-1">{v.name}</span>
            <SeverityBadge value={v.severity} />
          </div>
        ))}
      </div>
    </div>
  );
}; */

// Step 5 — data from /api/threat-sources/identify
const ThreatSourcesIdentifyDetail: React.FC<{ apiResponse: ThreatCoreResponse }> = ({ apiResponse }) => {
  const ts = apiResponse.threatSourcesDetail?.results?.threat_sources;
  if (!ts) return <p className="mt-2 text-xs text-green-600 font-medium">Step completed successfully</p>;

  const actors: any[] = ts.threat_actors ?? [];
  const vectors: any[] = ts.attack_vectors ?? [];

  return (
    <div className="mt-3 space-y-3">
      {actors.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-1.5">{actors.length} Threat Actor{actors.length !== 1 ? "s" : ""}</p>
          <div className="flex flex-wrap gap-1.5">
            {actors.map((a, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-50 text-orange-700 border border-orange-100 capitalize">
                {a.actor_type}
              </span>
            ))}
          </div>
        </div>
      )}
      {vectors.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-1.5">{vectors.length} Attack Vector{vectors.length !== 1 ? "s" : ""}</p>
          <div className="space-y-1.5">
            {vectors.map((v, i) => (
              <div key={i} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 flex items-start justify-between gap-2 flex-wrap">
                <span className="font-medium text-xs text-gray-800 flex-1">{v.name}</span>
                <SeverityBadge value={v.severity} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StepData: React.FC<{ stepId: string; apiResponse: ThreatCoreResponse }> = ({ stepId, apiResponse }) => {
  const results = apiResponse.results;

  if (stepId === "asset_extraction") return <AssetExtractionDetail results={results} />;
  if (stepId === "data_flow") return <DataFlowDetail results={results} />;
  if (stepId === "trust_boundary") return <TrustBoundaryDetail results={results} />;
 /*  if (stepId === "threat_source") return <ThreatSourceDetail results={results} />; */
  if (stepId === "threat_sources_identify") return <ThreatSourcesIdentifyDetail apiResponse={apiResponse} />;

  return <p className="mt-2 text-xs text-green-600 font-medium">Step completed successfully</p>;
};

// ── Main component ────────────────────────────────────────────────────────────

const ModelingProgress: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as ProcessingRouteState | null;

  // completedSteps: driven by /core/stream events (steps 1–4) and identifyThreatSources (step 5)
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  // apiResponse: populated from /api/threat/core + threatSourcesDetail from identifyThreatSources
  const [apiResponse, setApiResponse] = useState<ThreatCoreResponse>({});
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!routeState) {
      navigate("/console/threat-accelerator", { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!routeState) return;

    const controller = new AbortController();

    async function run() {
      let coreData: ThreatCoreResponse = {};
      let coreFailed = false;

      // ── Phase 1 (parallel) ────────────────────────────────────────────────
      // /core/stream     → display only: updates step progress indicators
      // /api/threat/core → data: assets, data_flows, trust_boundaries for dashboard
      await Promise.all([

        // Stream: progress display only
        new Promise<void>((resolve) => {
          streamThreatCore(
            routeState!.fileKey,
            "DEV_API_KEY_TMA",
            routeState!.assumptions ?? [],
            (_stepId, accumulated) => {
              setCompletedSteps([...(accumulated.steps_completed ?? [])]);
            },
            () => resolve(),
            (err) => {
              console.warn("[stream] display error:", err.message);
              resolve(); // non-fatal — data comes from callThreatCore
            },
            controller.signal
          );
        }),

        // /api/threat/core: actual response with full data
        callThreatCore(routeState!.fileKey, "DEV_API_KEY_TMA", routeState!.assumptions ?? [])
          .then((result) => { coreData = result; })
          .catch((err) => {
            coreFailed = true;
            setError(err.message);
          }),
      ]);

      if (controller.signal.aborted || coreFailed) {
        setIsComplete(true);
        return;
      }

      // Populate step detail cards with core data
      setApiResponse({ ...coreData });

      // Sync completed steps from the core API response (authoritative source)
      const coreSteps: string[] = Array.isArray(coreData.steps_completed) ? coreData.steps_completed : [];
      if (coreSteps.length > 0) {
        setCompletedSteps((prev) => Array.from(new Set([...prev, ...coreSteps])));
      }

      // /api/threat/core may return fields flat or nested under `results`
      const assets        = coreData.results?.assets         ?? coreData.assets.assets;
      const dataFlows     = coreData.results?.data_flows      ?? coreData.data_flows?.["Dataflows"];
      const trustBounds   = coreData.results?.trust_boundaries ?? coreData.trust_boundaries["boundaries"];

      // ── Phase 2: /api/threat-sources/identify ─────────────────────────────
      // Uses assets / data_flows / trust_boundaries from /api/threat/core response
      try {
        const threatResult = await identifyThreatSources(
          routeState!.fileKey,
          "DEV_API_KEY_TMA",
          assets,
          dataFlows,
          trustBounds
        );

        setApiResponse((prev) => ({ ...prev, threatSourcesDetail: threatResult }));
        setCompletedSteps((prev) => [...prev, "threat_sources_identify"]);
      } catch (err: any) {
        console.warn("[identifyThreatSources] failed:", err.message);
        setError(`Threat sources identification failed: ${err.message}`);
      }

      setIsComplete(true);
    }

    run();
    return () => controller.abort();
  }, []);

  if (!routeState) return null;

  const { title, description } = routeState;
  const completedSet = new Set(completedSteps);
  const processingStepId = !isComplete
    ? PIPELINE.find((s) => !completedSet.has(s.id))?.id ?? null
    : null;

  const completedCount = completedSteps.length;
  const totalSteps = PIPELINE.length;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* Top header bar */}
      <div className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3" />
          </svg>
          <div>
            <h2 className="text-base font-bold text-gray-900 leading-tight">{title || "Threat Model"}</h2>
            {description && <p className="text-xs text-gray-500 leading-tight mt-0.5">{description}</p>}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/console/threat-accelerator")}
            className="text-sm text-gray-500 hover:text-gray-700 transition font-medium"
          >
            ← Back
          </button>
          <button
            disabled={!isComplete}
            onClick={() =>
              navigate("/console/threat-accelerator/model", {
                state: {
                  fileKey: routeState?.fileKey ?? "",
                  title,
                  description,
                  threatSources: apiResponse.threatSourcesDetail ?? null,
                },
              })
            }
            className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-5 py-2 rounded-full font-semibold text-sm shadow hover:shadow-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            View Threat Model Dashboard
          </button>
        </div>
      </div>

      {/* Two-panel body */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left panel: Pipeline workflow ───────────────────────────────────── */}
        <div className="w-72 bg-white border-r border-gray-100 flex-shrink-0 overflow-y-auto">
          <div className="px-5 pt-6 pb-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">Pipeline Workflow</p>

            {/* Overall progress bar */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-500">{completedCount} / {totalSteps} steps</span>
                <span className="text-xs font-semibold text-purple-600">{Math.round((completedCount / totalSteps) * 100)}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-purple-500 to-green-500"
                  style={{ width: `${(completedCount / totalSteps) * 100}%` }}
                />
              </div>
              {error && <p className="mt-2 text-xs text-amber-600">{error}</p>}
            </div>
          </div>

          {/* Step list */}
          <div className="px-3 pb-6">
            <div className="relative">
              {/* Vertical connector line */}
              <div className="absolute left-[22px] top-5 bottom-5 w-0.5 bg-gray-100" />

              <div className="space-y-1">
                {PIPELINE.map((step, idx) => {
                  const isDone = completedSet.has(step.id);
                  const isProcessing = processingStepId === step.id;

                  return (
                    <div
                      key={step.id}
                      className={`relative flex items-start gap-3 rounded-xl px-3 py-3 transition-all duration-300 ${
                        isProcessing
                          ? "bg-purple-50 border border-purple-100"
                          : isDone
                          ? "bg-white"
                          : "opacity-40"
                      }`}
                    >
                      <div className="relative z-10 flex-shrink-0">
                        {isDone ? (
                          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center shadow-sm">
                            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ) : isProcessing ? (
                          <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center shadow-md shadow-purple-200">
                            <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full border-2 border-gray-200 bg-white flex items-center justify-center">
                            <span className="text-xs font-semibold text-gray-300">{idx + 1}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0 pt-0.5">
                        <p className={`text-sm font-semibold leading-tight ${
                          isDone ? "text-gray-800" : isProcessing ? "text-purple-700" : "text-gray-400"
                        }`}>
                          {step.label}
                        </p>
                        <p className={`text-xs mt-0.5 leading-relaxed ${
                          isDone || isProcessing ? "text-gray-500" : "text-gray-300"
                        }`}>
                          {step.description}
                        </p>
                        <p className="text-xs mt-1 font-medium">
                          {isDone && <span className="text-green-600">Completed</span>}
                          {isProcessing && <span className="text-purple-500 animate-pulse">Processing…</span>}
                          {!isDone && !isProcessing && <span className="text-gray-300">Pending</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ── Right panel: Progress / Results ─────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="max-w-2xl">

            {/* Status heading */}
            <div className="mb-6">
              {isComplete ? (
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-green-600 bg-green-50 border border-green-100 rounded-full px-4 py-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Analysis complete — all steps processed
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 text-sm text-purple-600 bg-purple-50 border border-purple-100 rounded-full px-4 py-1.5">
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
                  Analyzing your architecture…
                </div>
              )}
            </div>

            {/* Completed step detail cards */}
            <div className="space-y-4">
              {PIPELINE.filter((step) => completedSet.has(step.id)).map((step) => (
                <div key={step.id} className="bg-white rounded-xl border border-green-100 shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-sm text-gray-900">{step.label}</h4>
                    <span className="ml-auto text-xs text-green-600 font-medium">Completed</span>
                  </div>
                  <p className="text-xs text-gray-500 ml-7 mb-1">{step.description}</p>
                  <div className="ml-7">
                    <StepData stepId={step.id} apiResponse={apiResponse} />
                  </div>
                </div>
              ))}

              {/* Currently processing step placeholder */}
              {processingStepId && (
                <div className="bg-white rounded-xl border border-purple-200 shadow-md shadow-purple-50 p-5">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                      <div className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    </div>
                    <h4 className="font-semibold text-sm text-purple-700">
                      {PIPELINE.find((s) => s.id === processingStepId)?.label}
                    </h4>
                    <span className="ml-auto text-xs text-purple-500 font-medium animate-pulse">Processing…</span>
                  </div>
                  <p className="text-xs text-gray-500 ml-7 mt-1">
                    {PIPELINE.find((s) => s.id === processingStepId)?.description}
                  </p>
                  <div className="ml-7 mt-3 flex gap-1.5">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce"
                        style={{ animationDelay: `${i * 150}ms` }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state before processing starts */}
              {completedSteps.length === 0 && !processingStepId && (
                <div className="text-center py-16 text-gray-400">
                  <div className="w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center mx-auto mb-3">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" />
                  </div>
                  <p className="text-sm">Initializing pipeline…</p>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ModelingProgress;
